import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageLoader, PageHeader, money } from "@/components/shared";
import { Disclaimer } from "@/components/Disclaimer";
import { ClipboardList, MessageCircle } from "lucide-react";

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-4 py-1.5 border-b border-border/60 last:border-0">
      <dt className="text-muted-foreground shrink-0">{k}</dt>
      <dd className="text-right font-medium">{v || "—"}</dd>
    </div>
  );
}

export default function Onboarding() {
  const { data, isLoading } = useQuery({
    queryKey: ["workspace"],
    queryFn: async () => (await api.get("/buyer/workspace")).data,
  });

  if (isLoading) return <PageLoader />;

  const p = data?.client?.profile || {};
  const agentName = data?.agent?.name || "your agent";

  return (
    <div>
      <PageHeader title="My buying profile" subtitle={`This reflects what you shared with ${agentName} during your buyer consultation. No forms to fill out — just a snapshot of what matters to you.`} />

      {!p.completed ? (
        <Card className="p-10 rounded-2xl text-center" data-testid="profile-empty-state">
          <ClipboardList className="h-8 w-8 mx-auto text-primary mb-3" />
          <p className="font-medium">Your profile is on its way</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
            {agentName} will add your details here after your buyer consultation. Once it's in, you'll see everything you talked through — budget, must-haves, timeline and more.
          </p>
        </Card>
      ) : (
        <div className="space-y-5" data-testid="profile-readonly">
          <Card className="p-6 rounded-2xl">
            <h3 className="font-heading font-semibold text-lg mb-3">Budget & timeline</h3>
            <dl className="text-sm">
              <Row k="Budget" v={`${money(p.budget_min)} – ${money(p.budget_max)}`} />
              <Row k="Timeline" v={p.timeline} />
              <Row k="Financing status" v={p.financing_status} />
            </dl>
          </Card>

          <Card className="p-6 rounded-2xl">
            <h3 className="font-heading font-semibold text-lg mb-3">What you're looking for</h3>
            <dl className="text-sm">
              <Row k="Preferred areas" v={(p.areas || []).join(", ")} />
              <Row k="Workplace" v={p.workplace} />
              <Row k="Property type" v={p.property_type} />
              <Row k="Household" v={p.household_size} />
            </dl>
            {(p.must_haves || []).length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Must-haves</p>
                <div className="flex flex-wrap gap-2">
                  {p.must_haves.map((m) => <Badge key={m} className="bg-primary/10 text-primary border-0" data-testid="profile-musthave-chip">{m}</Badge>)}
                </div>
              </div>
            )}
            {(p.nice_to_haves || []).length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-muted-foreground mb-2">Nice-to-haves</p>
                <div className="flex flex-wrap gap-2">
                  {p.nice_to_haves.map((m) => <Badge key={m} className="bg-secondary text-foreground border-0">{m}</Badge>)}
                </div>
              </div>
            )}
            {p.deal_breakers && (
              <div className="mt-4">
                <p className="text-xs font-semibold text-muted-foreground mb-1">Deal-breakers</p>
                <p className="text-sm">{p.deal_breakers}</p>
              </div>
            )}
          </Card>

          {(p.comfort_level || p.questions) && (
            <Card className="p-6 rounded-2xl">
              <h3 className="font-heading font-semibold text-lg mb-3">Notes from your consultation</h3>
              {p.comfort_level && <p className="text-sm mb-3 whitespace-pre-wrap">{p.comfort_level}</p>}
              {p.questions && (
                <div className="p-3 rounded-xl bg-secondary">
                  <p className="text-xs font-semibold text-muted-foreground mb-1">Open questions to revisit</p>
                  <p className="text-sm whitespace-pre-wrap">{p.questions}</p>
                </div>
              )}
            </Card>
          )}

          <div className="flex items-start gap-2 text-sm text-muted-foreground p-4 rounded-xl bg-secondary/60" data-testid="profile-change-note">
            <MessageCircle className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
            <span>Something changed? Message {agentName} and they'll update this for you.</span>
          </div>
        </div>
      )}

      <div className="mt-6">
        <Disclaimer>This workspace organizes your preferences. It does not recommend specific homes, prices, or neighborhoods. Your agent and licensed professionals guide those decisions.</Disclaimer>
      </div>
    </div>
  );
}
